import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp, TableCompData, TabsComp } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import InvestorPortalPageHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection, {
  TableRowLink,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import {
  ServiceRequestStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { formatMoney } from "../../../../utils/currencyFormat";
import { useListStaffRealEstateRequestsQuery } from "../../../../store/admin/investorOperations/staffRealEstateApi";
import { SERVICE_REQUEST_STATUS } from "../../../../store/admin/investorOperations/constants";
import { useGetStaffListQuery } from "../../../../store/admin/staff";
import { mapInspectionFocusToRequestStatus } from "../../../../utils/staffRealEstateAdapter";
import moment from "moment";
import { withRealEstateGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: SERVICE_REQUEST_STATUS.SUBMITTED, label: "Submitted" },
  { key: SERVICE_REQUEST_STATUS.AWAITING_CONTACT, label: "Awaiting contact" },
  { key: SERVICE_REQUEST_STATUS.IN_PROGRESS, label: "In progress" },
  { key: SERVICE_REQUEST_STATUS.INSPECTION_SCHEDULED, label: "Inspection scheduled" },
  { key: SERVICE_REQUEST_STATUS.APPROVED, label: "Approved" },
  { key: SERVICE_REQUEST_STATUS.COMPLETED, label: "Completed" },
];

function staffLabel(staff) {
  return `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim() || staff?.email || `Staff #${staff?.id}`;
}

function RealEstateQueueTable({ statusFilter, sharedFilters }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const {
    search,
    accountType,
    assignedStaffId,
    inspectionFocus,
    fromDate,
    toDate,
  } = sharedFilters;

  const resolvedStatus =
    mapInspectionFocusToRequestStatus(inspectionFocus) || statusFilter || undefined;

  const apiQuery = useListStaffRealEstateRequestsQuery({
    page,
    per_page: PER_PAGE,
    status: resolvedStatus,
    account_type: accountType || undefined,
    assigned_staff_id: assignedStaffId || undefined,
    search: search || undefined,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
  });

  const pageData = useMemo(() => apiQuery.data?.items || [], [apiQuery.data]);
  const apiPagination = apiQuery.data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || pageData.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const openDetail = (row) => {
    navigate(
      `/super_admin/investor_portal/requests/${encodeURIComponent(row.requestReference || row.reference)}`
    );
  };

  const hasActiveFilters = Boolean(
    statusFilter ||
      search ||
      accountType ||
      assignedStaffId ||
      inspectionFocus ||
      fromDate ||
      toDate
  );

  const columns = [
    {
      name: "Reference",
      selector: (row) => row.requestReference,
      cell: (row) => (
        <TableRowLink onClick={() => openDetail(row)}>{row.requestReference}</TableRowLink>
      ),
      minWidth: "140px",
    },
    {
      name: "Customer",
      selector: (row) => row.customerName || "—",
      minWidth: "150px",
      grow: 1,
    },
    {
      name: "Type",
      cell: (row) => <ParticipantTypeBadge type={row.customerType || row.accountType} />,
      minWidth: "130px",
    },
    {
      name: "Property",
      selector: (row) => row.title || row.realEstate?.propertyName || "—",
      minWidth: "160px",
      grow: 1,
    },
    {
      name: "Location",
      selector: (row) => row.realEstate?.location || "—",
      minWidth: "130px",
    },
    {
      name: "Units",
      selector: (row) => row.realEstate?.unitsRequested ?? "—",
      minWidth: "70px",
      center: true,
    },
    {
      name: "Est. value",
      selector: (row) =>
        row.estimatedValueNgn != null ? formatMoney(row.estimatedValueNgn) : "—",
      minWidth: "120px",
      right: true,
    },
    {
      name: "Status",
      cell: (row) => <ServiceRequestStatusBadge status={row.status} />,
      minWidth: "140px",
    },
    {
      name: "Assigned",
      selector: (row) => row.assignedStaffName || "Unassigned",
      minWidth: "120px",
    },
    {
      name: "Inspection",
      selector: (row) =>
        row.inspectionDate ? moment(row.inspectionDate).format("DD-MMM-YYYY") : "—",
      minWidth: "110px",
    },
    {
      name: "Submitted",
      selector: (row) =>
        row.submittedAt ? moment(row.submittedAt).format("DD-MMM-YYYY") : "—",
      minWidth: "110px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <TableActionDropdown
          items={[{ name: "View details", action: () => openDetail(row) }]}
          row={row}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  const emptyState = apiQuery.isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load real-estate requests."
      onCta={() => apiQuery.refetch()}
    />
  ) : hasActiveFilters ? (
    <InvestorPortalTableEmpty
      variant="filtered"
      title="No requests match this queue."
      description="Try another status tab or clear your filters."
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No property requests require attention."
      description="Property interest requests from investors and members appear here."
    />
  );

  return (
    <InvestorPortalTableSection pagination={pagination}>
      <TableCompData
        columns={columns}
        data={pageData}
        progressPending={apiQuery.isFetching}
        progressComponent={<TableSkeletonLoader rows={8} columns={10} />}
        noDataComponent={emptyState}
      />
    </InvestorPortalTableSection>
  );
}

function RealEstateRequestsPage() {
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState("");
  const [assignedStaffId, setAssignedStaffId] = useState("");
  const [inspectionFocus, setInspectionFocus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: staffListData } = useGetStaffListQuery(1);
  const staffOptions = useMemo(() => staffListData?.staffs || [], [staffListData?.staffs]);

  const sharedFilters = {
    search,
    accountType,
    assignedStaffId,
    inspectionFocus,
    fromDate,
    toDate,
  };

  const clearFilters = () => {
    setSearch("");
    setAccountType("");
    setAssignedStaffId("");
    setInspectionFocus("");
    setFromDate("");
    setToDate("");
  };

  const useTab = STATUS_TABS.map((tab) => ({
    name: tab.label,
    component: (
      <RealEstateQueueTable
        key={`${tab.key}-${search}-${accountType}-${assignedStaffId}-${inspectionFocus}-${fromDate}-${toDate}`}
        statusFilter={tab.key}
        sharedFilters={sharedFilters}
      />
    ),
  }));

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Real Estate Requests"
        subtitle="Review property interest requests, schedule inspections, and update status."
      />

      <CardComp cardClassName="border-0 shadow-sm mb-3 investor-portal-filter-bar">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4 col-lg-3">
            <label className="form-label small fw-semibold">Search</label>
            <input
              className="form-control"
              placeholder="Reference, customer, property…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-4 col-lg-2">
            <label className="form-label small fw-semibold">Account type</label>
            <select
              className="form-select"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
            >
              <option value="">All</option>
              <option value="investor">Investor</option>
              <option value="member">Member</option>
            </select>
          </div>
          <div className="col-6 col-md-4 col-lg-2">
            <label className="form-label small fw-semibold">Assigned staff</label>
            <select
              className="form-select"
              value={assignedStaffId}
              onChange={(e) => setAssignedStaffId(e.target.value)}
            >
              <option value="">All staff</option>
              {staffOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {staffLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-4 col-lg-2">
            <label className="form-label small fw-semibold">Inspection</label>
            <select
              className="form-select"
              value={inspectionFocus}
              onChange={(e) => setInspectionFocus(e.target.value)}
            >
              <option value="">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="col-6 col-md-4 col-lg-1">
            <label className="form-label small fw-semibold">From</label>
            <input
              type="date"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-4 col-lg-1">
            <label className="form-label small fw-semibold">To</label>
            <input
              type="date"
              className="form-control"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="col-12 col-lg-1">
            <button type="button" className="btn btn-outline-secondary w-100" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
        <p className="text-muted small mb-0 mt-2">
          Inspection filter maps to request status (backend has no separate inspection_status
          param). Assigned staff list uses the first page of company staff.
        </p>
      </CardComp>

      <TabsComp
        TabArray={useTab}
        input="name"
        justifyContent="justify-content-start"
        tabClassName="px-0"
        TabArrayClassName="px-1"
      />
    </div>
  );
}

export default withRealEstateGuard(RealEstateRequestsPage);
